import cuid from 'cuid'
import type { TempTrack } from 'types/shortlist'

import jsmediatags from '@disco/jsmediatags-browser'

/**
 * Extracts a file title from a filename by removing the file extension.
 *
 * @param name - The filename including extension
 * @returns The filename without the extension
 *
 * @example
 * ```typescript
 * getFileTitle('song.mp3') // returns 'song'
 * getFileTitle('my.favorite.song.flac') // returns 'my.favorite.song'
 * getFileTitle('document') // returns 'document'
 * ```
 */
export function getFileTitle(name: string): string {
  const parts = name.split('.')
  if (parts.length) parts.pop()
  return parts.join('.')
}

type CrossPlatformFile = File & {
  urn?: string
}

/**
 * Extracts ID3 metadata tags from an audio/video file.
 *
 * Attempts to parse media file metadata (title, artist, album) using jsmediatags.
 * If parsing fails or file is unsupported, falls back to using the filename as title.
 *
 * @param file - The audio/video file to extract metadata from
 * @param id - Optional unique ID for the track, generated if not provided
 * @returns Promise resolving to a partial TempTrack object with the extracted metadata
 *
 * @example
 * ```typescript
 * // Extract tags from an MP3 file
 * const fileInput = document.querySelector('input[type="file"]');
 * const file = fileInput.files[0];
 *
 * getID3Tags(file).then(trackData => {
 *   console.log('Track Title:', trackData.title);
 *   console.log('Artist:', trackData.artist);
 *   console.log('Album:', trackData.album);
 * });
 *
 * // With custom ID
 * getID3Tags(file, 'custom-track-123').then(trackData => {
 *   console.log('Track ID:', trackData.tempId); // 'custom-track-123'
 * });
 * ```
 */
export function getID3Tags(
  file: CrossPlatformFile,
  id?: string | null,
): Promise<Partial<TempTrack>> {
  return new Promise(resolve => {
    return parseMediaTags(file)
      .then(tags => {
        const data = {
          tempId: id || cuid(),
          title: tags['title'] || getFileTitle(file.name),
          album: tags['album'],
          artist: tags['artist'],
          filename: file.urn || file.name,
          progress: 0,
        } as const
        return resolve(data)
      })
      .catch(() =>
        resolve({
          tempId: id || cuid(),
          ...getEmptyTags(file),
          filename: file.urn || file.name,
          progress: 0,
        }),
      )
  })
}

const maxFileSize = 100 * 1024 * 1024

/**
 * Gets the lowercase file extension from a filename.
 *
 * @param name - The filename including extension
 * @returns The lowercase file extension without the dot
 *
 * @example
 * ```typescript
 * getFileExtension('song.MP3') // returns 'mp3'
 * getFileExtension('document.PDF') // returns 'pdf'
 * getFileExtension('image.with.dots.JPG') // returns 'jpg'
 * ```
 */
const getFileExtension = (name: string): string =>
  name.split('.').pop()?.toLowerCase() || ''

const getEmptyTags = file => ({
  title: getFileTitle(file.name),
})

const supportedTypes = [
  'm4v',
  'webm',
  'ogv',
  'mp4',
  'mov',
  'flv',
  'wmv',
  '3gp',
  'mpeg',
  'mpg',
  'mp3',
  'm4a',
  'ogg',
  'aac',
  'wav',
  'wma',
  'aiff',
  'aif',
  'flac',
]

const knownTags = ['title', 'artist', 'album']

const skipParsing = file => {
  const extension = getFileExtension(file.name)
  const unsupportedExtension = !supportedTypes.includes(extension)
  const oversized = file.size > maxFileSize
  return unsupportedExtension || oversized
}

/**
 * Parses media tags from an audio/video file using jsmediatags.
 *
 * Skips parsing for unsupported file types or files larger than the size limit.
 * Only extracts a subset of known tags (title, artist, album).
 *
 * @param file - The audio/video file to parse
 * @returns Promise resolving to an object containing the parsed tags
 *
 * @example
 * ```typescript
 * // Parse tags from an audio file
 * const audioFile = new File([audioBlob], 'song.mp3', { type: 'audio/mp3' });
 *
 * parseMediaTags(audioFile).then(tags => {
 *   console.log('Parsed Tags:', tags);
 *   // { title: 'Song Name', artist: 'Artist Name', album: 'Album Name' }
 * });
 *
 * // For unsupported or large files
 * const largeFile = new File([bigBlob], 'large.mp3', { type: 'audio/mp3' });
 * // File size > maxFileSize
 * parseMediaTags(largeFile).then(tags => {
 *   console.log('Tags for large file:', tags);
 *   // { title: 'large' } - only contains filename without extension
 * });
 * ```
 */
const parseMediaTags = (file): Promise<Partial<TempTrack>> =>
  new Promise(
    (
      resolve: (
        result:
          | Promise<{
              title: string
            }>
          | {
              title: string
            },
      ) => void,
      reject: (error?: any) => void,
    ) => {
      if (skipParsing(file)) {
        // console.warn(`File ${file.name} is too big or has unsupported extension, skipping tags parsing`)
        resolve(getEmptyTags(file))
      } else {
        let blob = new Blob([file])
        // @ts-expect-error unknown type
        const reader = new jsmediatags.Reader(blob).setTagsToRead(knownTags)
        reader.read({
          onSuccess: result => {
            const tags = result.tags || getEmptyTags(file)
            resolve(tags)
          },
          onError: error => {
            const thrownError = error.info ? new Error(error.info) : error
            reject(thrownError)
          },
        })
      }
    },
  )
